<template>
  <header
    :class="$style.mainHeader"
    :data-test-id="$testId('mainHeader')"
    @focusin="handleFocusIn"
  >
    <div :class="$style.headerTextContainer">
      <h1
        v-if="returnPath === ''"
        :class="$style.headerTitle"
      >
        {{ title }}
      </h1>
      <ButtonBack
        v-else
        :text="title"
        :to="returnPath"
      />
      <GettingStartedIconTooltip
        v-bind="tooltipProps"
        :class="$style.gettingStartedTooltip"
      />

      <div v-if="premiumBadgeProps.visible">
        <FairUseCountdownBadge
          v-if="premiumBadgeProps.showFeatureUsage"
          :data-test-id="$testId('premiumFeatureBadge')"
          :featureCode="feature"
          @click="handleLearnMoreClick"
        />
        <PremiumFeatureBadge
          v-else
          :data-test-id="$testId('premiumFeatureBadge')"
        />
      </div>
    </div>
    <div
      :class="$style.headerItems"
    >
      <HeaderItemProductTour
        v-if="showProductTour"
        @click="handleNavMenuItemClicked('product tour')"
      />
      <HeaderItemPricing
        v-if="showPricingButton"
        data-test-id="MainHeader__HeaderItemPricing"
        @click="(payload) => handleNavMenuItemClicked('pricing', payload)"
      />
      <HeaderItemNotifications
        :notificationsCount="notificationsCount"
        @click="handleNavMenuItemClicked('notifications text')"
      />
      <HeaderItemWhatsNew
        @click="handleNavMenuItemClicked('resources text')"
        @openWhatsNewPanel="handleOpenWhatsNewPanel"
      />
      <HeaderItemSupport
        data-test-id="MainHeader__HeaderItemSupport"
        @click="handleNavMenuItemClicked('support text')"
      />
      <HeaderItemChecklist
        v-if="showImplementationChecklist"
        @click="(payload) => handleNavMenuItemClicked('checklist', payload)"
      />
    </div>
    <UserMenu
      :isProviderOrg="isProviderOrg"
    />
  </header>
</template>

<script>
import { ButtonBack } from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapGetters, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FairUseCountdownBadge from 'components/Badges/FairUseCountdownBadge.vue';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import GettingStartedIconTooltip from 'components/Tooltip/GettingStartedIconTooltip.vue';
import HeaderItemChecklist from 'components/MainHeader/HeaderItemChecklist.vue';
import HeaderItemNotifications from 'components/MainHeader/HeaderItemNotifications.vue';
import HeaderItemPricing from 'components/MainHeader/HeaderItemPricing.vue';
import HeaderItemProductTour from 'components/MainHeader/HeaderItemProductTour.vue';
import HeaderItemSupport from 'components/MainHeader/HeaderItemSupport.vue';
import HeaderItemWhatsNew from 'client/components/MainHeader/HeaderItemWhatsNew.vue';
import MspState from 'client/components/models/MspState';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import PremiumFeatureBadge from 'components/Badges/PremiumFeatureBadge.vue';
import UserMenu from 'components/MainHeader/UserMenu.vue';
import Util from 'client/util/Util';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const {
  description,
  location,
  section,
  text,
  toUrl,
} = eventProperties;

const {
  upperNavigation,
} = eventLocations;

export default {
  name: 'MainHeader',

  Util,
  EventHelper,
  OptimizelyHelper,

  components: {
    ButtonBack,
    FairUseCountdownBadge,
    GettingStartedIconTooltip,
    HeaderItemChecklist,
    HeaderItemNotifications,
    HeaderItemPricing,
    HeaderItemProductTour,
    HeaderItemWhatsNew,
    HeaderItemSupport,
    PremiumFeatureBadge,
    UserMenu,
  },

  mixins: [FeatureTrialsMixin],

  props: {
    feature: {
      type: String,
      default: '',
    },
    isProviderOrg: Boolean,
    notificationsCount: {
      type: Number,
      default: 0,
    },
    returnPath: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    tooltipProps: {
      type: Object,
      default: () => ({
        pText: '',
      }),
    },
  },

  computed: {
    ...mapGetters('FeatureTrialModule', [
      'getPremiumBadgeProps',
    ]),

    ...mapState(settingsModule, [
      'isCustomer',
    ]),

    ...mapGetters(settingsModule, [
      'hasBilling',
      'isProvider',
      'isCommandRunner',
    ]),

    modalProps() {
      const { modalProps } = this.$route.meta;
      return modalProps || {};
    },

    premiumBadgeProps() {
      return this.getPremiumBadgeProps(this.feature, this.$route.meta);
    },

    showImplementationChecklist() {
      return !this.isCommandRunner && !this.$options.Util.isSmallScreen();
    },

    showPricingButton() {
      return (this.hasBilling && !this.isCustomer)
        // is Provider checks the provider user (i.e. the head MTP admin)
        && !MspState.isAnMspOrChild;
    },

    showProductTour() {
      return !this.$options.Util.isSmallScreen() && !this.isCommandRunner;
    },
  },

  created() {
    this.fetchEntitledFeatureCodes();
  },

  methods: {
    handleFocusIn(event) {
      this.$emit('focusin', event);
    },

    handleLearnMoreClick() {
      EventHelper.triggerLinkClicked({
        [description]: `learn more click from ${this.modalProps.featureName} feature page`,
        [location]: `${this.modalProps.featureName} Page`,
        [section]: 'top',
        [text]: 'Learn More',
        [toUrl]: this.modalProps.eventLocation,
      });
    },

    handleNavMenuItemClicked(navMenuItem, payload = {}) {
      this.$options.EventHelper.triggerNavMenuItemClicked({
        ...payload,
        [location]: upperNavigation,
        navMenuItemClicked: navMenuItem,
      });
    },

    handleOpenWhatsNewPanel() {
      this.$emit('openWhatsNew');
    },
  },
};
</script>

<style module>
@import './HeaderItem.css';

@value (headerHeight) from '~css/layout.css';
@value (max-width-large) from '~css/breakpoints.css';

.mainHeader {
  --jc-icon-offset: -0.19em;

  align-items: center;
  background: white;
  border-bottom: 1px solid var(--jc-border-color);
  display: flex;
  gap: 20px;
  grid-area: mainHeader;
  height: headerHeight;
  justify-content: space-between;
  min-width: 0;
  padding: 0 20px;
  position: relative;
}

.headerTextContainer {
  align-items: center;
  display: flex;
  gap: 10px;
}

.headerTitle {
  display: inline-block;
  font-size: 24px;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gettingStartedTooltip {
  line-height: 1;
  margin-left: 10px;
}

.headerItems {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 20px;
  justify-content: flex-end;
}

.supportText,
.supportText:hover,
.supportText:focus {
  text-decoration: none;
}

@media (max-width: max-width-large) {
  .mainHeader {
    gap: 12px;
  }

  .headerTitle {
    font-size: 16px;
  }

  .headerItems {
    gap: 8px;
  }
}
</style>
